import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Check, Copy, Grid, List, Loader2, Search, Trash2, Upload, Video, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { AdminLayout } from "@/components/admin/admin-layout";
import { Button } from "@/components/button";
import { usePageTitle } from "@/hooks/use-page-title";

interface Media {
  id: string;
  filename: string;
  url: string;
  type: string;
  mime_type: string | null;
  size: number | null;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  created_at: string;
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "Unknown size";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MediaLibrary() {
  usePageTitle("Media Library");

  const queryClient = useQueryClient();
  const authenticatedFetch = useAuthenticatedFetch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Fetch media
  const { data: mediaData, isLoading } = useQuery({
    queryKey: ["admin-media"],
    queryFn: async () => {
      const res = await authenticatedFetch("/api/admin/media");
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json() as Promise<{ data: Media[] }>;
    },
  });

  // Upload mutation - accepts File[] (not FileList) to avoid reference issues
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("files", file);
      });

      const res = await authenticatedFetch("/api/admin/media/upload", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const error = (await res.json()) as { message?: string };
        throw new Error(error.message || "Failed to upload files");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-media"] });
      toast.success("Files uploaded successfully");
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Failed to upload files";
      toast.error(message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (mediaId: string) => {
      const res = await authenticatedFetch(`/api/admin/media/${mediaId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete media");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-media"] });
      setSelectedMedia(null);
      toast.success("Media deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete media");
    },
  });

  // Update alt text mutation
  const updateAltMutation = useMutation({
    mutationFn: async ({ id, altText }: { id: string; altText: string }) => {
      const res = await authenticatedFetch(`/api/admin/media/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ alt_text: altText }),
      });
      if (!res.ok) throw new Error("Failed to update alt text");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-media"] });
      toast.success("Alt text updated");
    },
    onError: () => {
      toast.error("Failed to update alt text");
    },
  });

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      // IMPORTANT: Convert FileList to Array BEFORE clearing input!
      // FileList is a live reference that gets cleared when input is reset
      const filesArray = Array.from(fileList);
      e.target.value = "";
      uploadMutation.mutate(filesArray);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const fileList = e.dataTransfer.files;
      if (fileList && fileList.length > 0) {
        // Convert FileList to Array to avoid reference issues
        const filesArray = Array.from(fileList);
        uploadMutation.mutate(filesArray);
      }
    },
    [uploadMutation]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const copyUrl = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("URL copied to clipboard");
  };

  const selectMedia = (media: Media) => {
    setSelectedMedia(media);
  };

  const media = mediaData?.data || [];
  const filteredMedia = media.filter((m) => {
    const matchesSearch = m.filename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || m.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <AdminLayout>
      <div className="flex h-full">
        {/* Main area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-6 pb-0">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-semibold text-foreground">Media Library</h1>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button onClick={handleUpload} disabled={uploadMutation.isPending}>
                  {uploadMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm bg-secondary border-0 rounded-md focus:ring-2 focus:ring-ring focus:outline-none"
                />
              </div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="text-sm bg-secondary border-0 rounded-md px-3 py-2 focus:ring-2 focus:ring-ring focus:outline-none"
              >
                <option value="all">All Types</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
              </select>
              <div className="flex items-center border border-border rounded-md overflow-hidden">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 ${viewMode === "grid" ? "bg-accent" : "hover:bg-accent/50"}`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 ${viewMode === "list" ? "bg-accent" : "hover:bg-accent/50"}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Drop zone / Media grid */}
          <div
            className="flex-1 overflow-auto p-6 pt-0"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredMedia.length === 0 ? (
              <div
                className="h-64 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-muted-foreground/50 transition-colors"
                onClick={handleUpload}
              >
                <Upload className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground text-sm">
                  {searchQuery || typeFilter !== "all"
                    ? "No files match your filters"
                    : "Drop files here or click to upload"}
                </p>
                <p className="text-muted-foreground/70 text-xs mt-1">Supports images and videos</p>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredMedia.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => selectMedia(item)}
                    className={`group relative aspect-square bg-secondary rounded-lg overflow-hidden cursor-pointer border-2 transition-colors ${
                      selectedMedia?.id === item.id
                        ? "border-primary"
                        : "border-transparent hover:border-border"
                    }`}
                  >
                    {item.type === "image" ? (
                      <img
                        src={item.url}
                        alt={item.alt_text || item.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                      <div className="p-2 w-full">
                        <p className="text-white text-xs truncate">{item.filename}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg divide-y divide-border">
                {filteredMedia.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => selectMedia(item)}
                    className={`flex items-center gap-4 p-3 hover:bg-accent/50 cursor-pointer ${
                      selectedMedia?.id === item.id ? "bg-accent/50" : ""
                    }`}
                  >
                    <div className="w-12 h-12 bg-secondary rounded overflow-hidden flex-shrink-0">
                      {item.type === "image" ? (
                        <img
                          src={item.url}
                          alt={item.alt_text || item.filename}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {item.filename}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(item.size)} ·{" "}
                        {format(new Date(item.created_at), "MMM d, yyyy")}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyUrl(item.url, item.id);
                      }}
                      className="p-2 text-muted-foreground hover:text-foreground"
                    >
                      {copiedId === item.id ? (
                        <Check className="h-4 w-4 text-success" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Details sidebar */}
        {selectedMedia && (
          <aside className="w-80 border-l border-border bg-card overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium text-foreground">Details</h2>
              <button
                onClick={() => setSelectedMedia(null)}
                className="p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Preview */}
            <div className="p-4 border-b border-border">
              <div className="aspect-video bg-secondary rounded-lg overflow-hidden flex items-center justify-center">
                {selectedMedia.type === "image" ? (
                  <img
                    src={selectedMedia.url}
                    alt={selectedMedia.alt_text || selectedMedia.filename}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <video src={selectedMedia.url} controls className="max-w-full max-h-full" />
                )}
              </div>
            </div>

            {/* Info */}
            <div className="p-4 border-b border-border space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Filename</label>
                <p className="text-sm text-foreground break-all">{selectedMedia.filename}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Type</label>
                <p className="text-sm text-foreground">
                  {selectedMedia.mime_type || selectedMedia.type}
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Size</label>
                <p className="text-sm text-foreground">{formatFileSize(selectedMedia.size)}</p>
              </div>
              {selectedMedia.width && selectedMedia.height && (
                <div>
                  <label className="text-xs text-muted-foreground">Dimensions</label>
                  <p className="text-sm text-foreground">
                    {selectedMedia.width} x {selectedMedia.height}
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs text-muted-foreground">Uploaded</label>
                <p className="text-sm text-foreground">
                  {format(new Date(selectedMedia.created_at), "MMM d, yyyy 'at' h:mm a")}
                </p>
              </div>
            </div>

            {/* URL */}
            <div className="p-4 border-b border-border">
              <label className="text-xs text-muted-foreground">URL</label>
              <div className="mt-1 flex gap-2">
                <input
                  type="text"
                  value={selectedMedia.url}
                  readOnly
                  className="flex-1 text-xs bg-secondary border-0 rounded px-2 py-1.5 text-muted-foreground"
                />
                <button
                  onClick={() => copyUrl(selectedMedia.url, selectedMedia.id)}
                  className="px-2 py-1.5 bg-secondary text-foreground rounded text-xs hover:bg-accent transition-colors"
                >
                  {copiedId === selectedMedia.id ? (
                    <Check className="h-3 w-3 text-success" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>

            {/* Alt text */}
            <div className="p-4 border-b border-border">
              <label className="text-xs text-muted-foreground">Alt Text</label>
              <textarea
                value={selectedMedia.alt_text || ""}
                onChange={(e) => {
                  setSelectedMedia({
                    ...selectedMedia,
                    alt_text: e.target.value,
                  });
                }}
                onBlur={(e) => {
                  updateAltMutation.mutate({
                    id: selectedMedia.id,
                    altText: e.target.value,
                  });
                }}
                rows={2}
                placeholder="Describe this image..."
                className="mt-1 w-full text-sm border border-border rounded-md px-3 py-2 bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Actions */}
            <div className="p-4">
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => {
                  if (confirm("Are you sure you want to delete this file?")) {
                    deleteMutation.mutate(selectedMedia.id);
                  }
                }}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete
              </Button>
            </div>
          </aside>
        )}
      </div>
    </AdminLayout>
  );
}
